import { FitBounds } from 'react-mapbox-gl/lib/map';
import { atom, DefaultValue, selector } from 'recoil';
import { isEqual, isNil } from 'lodash';
import { loadPersistedValue, logOnSet, persistOnSet } from '../effects';
import { MapAssetType } from '../../contexts/AggregatesContext/types';
import { MapBounds } from '../../contexts/FilterContext/types';
import { selectedFloorPlanState } from './floorPlan';
import { configState } from './config';
import { MapTypes } from '../../contexts/mapStyle';

export const mapBoundsState = atom<MapBounds | null>({
  key: 'mapBoundsState',
  default: null,
});

export const floorPlanMapBoundsState = atom<MapBounds | null>({
  key: 'floorPlanMapBoundsState',
  default: null,
});

export const mapBoundsSelector = selector<MapBounds | null>({
  key: 'mapBoundsSelector',
  get: ({ get }) => {
    const selectedFloorPlan = get(selectedFloorPlanState);
    const mapBounds = get(mapBoundsState);
    const floorPlanBounds = get(floorPlanMapBoundsState);
    return isNil(selectedFloorPlan) ? mapBounds : floorPlanBounds;
  },
  set: ({ get, reset, set }, newValue) => {
    const selectedFloorPlan = get(selectedFloorPlanState);
    const stateToSet = isNil(selectedFloorPlan) ? mapBoundsState : floorPlanMapBoundsState;
    if (newValue instanceof DefaultValue) {
      reset(stateToSet);
    } else {
      set(stateToSet, newValue);
    }
  },
});

export const mapCenterState = atom<[number, number]>({
  key: 'mapMaintenance.mapCenterState',
  default: selector({
    key: 'mapMaintenance.mapCenterState.default',
    get: ({ get }) => {
      const { lat, lon } = get(configState).initialMapExtents ?? {};
      return [parseFloat(lon) || 0, parseFloat(lat) || 0];
    },
  }),
  effects: [loadPersistedValue, persistOnSet],
});

export const floorPlanMapCenterState = atom<[number, number]>({
  key: 'mapMaintenance.floorPlanMapCenterState',
  default: [0, 0],
  effects: [loadPersistedValue, persistOnSet],
});

export const mapCenterSelector = selector({
  key: 'mapMaintenance.mapCenterSelector',
  get: ({ get }) => {
    const selectedFloorPlan = get(selectedFloorPlanState);
    const mapCenter = get(mapCenterState);
    const floorPlanMapCenter = get(floorPlanMapCenterState);
    return isNil(selectedFloorPlan) ? mapCenter : floorPlanMapCenter;
  },
  set: ({ get, reset, set }, newValue) => {
    const selectedFloorPlan = get(selectedFloorPlanState);
    const stateToSet = isNil(selectedFloorPlan) ? mapCenterState : floorPlanMapCenterState;
    newValue instanceof DefaultValue ? reset(stateToSet) : set(stateToSet, newValue);
  },
});

export const mapZoomLevelState = atom<number>({
  key: 'mapMaintenance.mapZoomLevelState',
  default: selector({
    key: 'mapMaintenance.mapZoomLevelState.default',
    get: ({ get }) => get(configState).initialMapExtents?.zoom || 10,
  }),
  effects: [loadPersistedValue, persistOnSet],
});

export const floorPlanMapZoomLevelState = atom<number>({
  key: 'mapMaintenance.floorPlanMapZoomLevelState',
  default: 1,
  effects: [loadPersistedValue, persistOnSet],
});

export const mapZoomLevelSelector = selector({
  key: 'mapMaintenance.mapZoomLevelSelector',
  get: ({ get }) => {
    const selectedFloorPlan = get(selectedFloorPlanState);
    const mapZoomLevel = get(mapZoomLevelState);
    const floorPlanMapZoomLevel = get(floorPlanMapZoomLevelState);
    return isNil(selectedFloorPlan) ? mapZoomLevel : floorPlanMapZoomLevel;
  },
  set: ({ get, reset, set }, newValue) => {
    const selectedFloorPlan = get(selectedFloorPlanState);
    const stateToSet = isNil(selectedFloorPlan) ? mapZoomLevelState : floorPlanMapZoomLevelState;
    newValue instanceof DefaultValue ? reset(stateToSet) : set(stateToSet, newValue);
  },
});

export const mapSelectBoundsState = atom<MapBounds | null>({
  key: 'mapSelectBoundsState',
  default: null,
});

export const mapLocalBoundsState = atom<FitBounds | undefined>({
  key: 'mapMaintenance.mapLocalBoundsState',
  default: undefined,
  effects: [loadPersistedValue, persistOnSet],
});

export const forceMapReloadState = atom<boolean>({
  key: 'forceMapReloadState',
  default: false,
});

export const cardIdsState = atom<string[]>({
  key: 'cardIdsState',
  default: [],
});

export const selectedItemsState = atom<MapAssetType[]>({
  key: 'selectedItemsState',
  default: [],
});

export const mapCardHeightState = atom<number>({
  key: 'mapCardHeightState',
  default: 0,
});

export const mapTypeState = atom<MapTypes>({
  key: 'mapTypeState',
  default: 'satellite-hybrid',
  effects: [persistOnSet, loadPersistedValue]
});
